import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Image } from 'antd';
import { LoadingOutlined, PlayCircleFilled } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

/*rgba(255, 234, 188, .5)   ECF1F3*/
const MessageBlockWrapper = styled.p`
  padding: 10px;
  border-radius: 10px;
  font-size: 16px;
  background: ${(props) => (props.isMe ? `#cbc9c9` : `rgba(255, 234, 188, .5)`)};

  ${(props) =>
    props.isMe
      ? css`
          color: #fff;
          margin-left: 10px;
        `
      : `margin-right:10px;`}

  .msg_image {
    display: block;
  }

  video::-webkit-media-controls-enclosure {
    width: 100%;
    background-color: transparent;
    -moz-border-radius: 1px !important;
    -webkit-border-radius: 1px !important;
    border-radius: 1px !important;
    filter: alpha(opacity=80);
    opacity: 0.8;
    margin-bottom: -20px;
    overflow: hidden;
  }
  video::-webkit-media-controls-panel {
    background-color: #eee;
    height: 35px !important;
  }

  audio::-webkit-media-controls-enclosure {
    width: 100%;
    background-color: transparent;
    -moz-border-radius: 5px !important;
    -webkit-border-radius: 5px !important;
    border-radius: 5px !important;
    filter: alpha(opacity=80);
    opacity: 0.8;
    overflow: hidden;
  }
  audio::-webkit-media-controls-panel {
    background-color: #eee;
  }
`;

const MarkdownComponents = {
  img: (image) => {
    return <Image rootClassName="msg_image" src={image.src} style={{ width: '100%', padding: '2%', maxWidth: '200px' }} />;
  },
  a: (link) => {
    let href = link.href;
    if (href.endsWith('.mp4')) {
      return (
        <video controls controlsList="nodownload noplaybackrate noremoteplayback " style={{ width: '100%', padding: '2%' }}>
          <source src={href} type="video/mp4" />
        </video>
      );
    }
    if (href.endsWith('.mp3')) {
      return (
        <audio controls controlsList="nodownload noplaybackrate noremoteplayback " style={{ width: '100%', padding: '2%' }}>
          <source src={href} type="audio/mpeg" />
        </audio>
      );
    }
    if (href.includes('.jpg') || href.includes('.jpeg') || href.includes('.png') || href.includes('.gif')) {
      return <Image rootClassName="msg_image" src={href} style={{ width: '100%', padding: '2%', maxWidth: '200px' }} />;
    }

    return <a href={link.href}>{link.children}</a>;
  },
};

const MessageBlock = ({ isMe, message, urls = [], type = '', currentFileList = [], simpleAudioButton = false }) => {
  // let capitalizedMessage = message?.charAt(0).toUpperCase() + message?.slice(1);
  // check if single \n withing words and replace it with double \n
  let newLineMessage = message
    .replace(/```markdown/g, '')
    .replace(/```/g, '')
    .replace('\\n\\n', '\n\n')
    .replace(/\r\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\*\*\n(?=\w)/g, '**\n\n')
    .replace(/\\n\\n(?=\w)/g, '\n\n ')
    .replace(/\\n(?=\w)/g, ' \n ')
    .replace(/`/g, "'");

  const audioRef = useRef(null);
  const unmuteTimerRef = useRef(null);
  const shouldKeepMutedRef = useRef(false);
  const playRequestIdRef = useRef(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);

  const firstAudioData = useMemo(() => {
    const firstAudio = urls.find((urlItem) => urlItem?.type === 'audio' && urlItem?.url);
    return firstAudio || null;
  }, [urls]);

  const firstAudioUrl = firstAudioData?.url || null;
  const parseNumeric = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const clipStartSec = useMemo(() => {
    if (!firstAudioData) return 0;
    const startSec = parseNumeric(firstAudioData.startSec);
    if (startSec !== null) return Math.max(0, startSec);
    const startMs = parseNumeric(firstAudioData.startMs);
    if (startMs !== null) return Math.max(0, startMs / 1000);
    return 0;
  }, [firstAudioData]);

  const clipEndSec = useMemo(() => {
    if (!firstAudioData) return null;
    const endSec = parseNumeric(firstAudioData.endSec);
    if (endSec !== null) return Math.max(0, endSec);
    const endMs = parseNumeric(firstAudioData.endMs);
    if (endMs !== null) return Math.max(0, endMs / 1000);
    return null;
  }, [firstAudioData]);

  const clearUnmuteTimer = () => {
    if (unmuteTimerRef.current) {
      clearTimeout(unmuteTimerRef.current);
      unmuteTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearUnmuteTimer();
      shouldKeepMutedRef.current = false;
      playRequestIdRef.current += 1;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.muted = false;
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    clearUnmuteTimer();
    shouldKeepMutedRef.current = false;
    setIsAudioLoading(false);
    setIsAudioPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.muted = false;
      audioRef.current.currentTime = clipStartSec || 0;
      audioRef.current.load();
    }
  }, [firstAudioUrl, clipStartSec]);

  const handleToggleAudio = async (e) => {
    e.stopPropagation();
    if (!audioRef.current) {
      return;
    }

    const audioEl = audioRef.current;
    const startAtSec = clipStartSec || 0;
    const playRequestId = playRequestIdRef.current + 1;
    playRequestIdRef.current = playRequestId;
    const seekTolerance = 0.08;
    const seekWaitMs = 6000;
    const seekPollMs = 120;
    const forceSeekToStart = async () => {
      if (!(startAtSec > 0)) {
        return true;
      }
      const startedAt = Date.now();
      while (Date.now() - startedAt < seekWaitMs) {
        try {
          audioEl.currentTime = startAtSec;
        } catch (error) {
          // Ignore temporary seek errors until metadata is ready.
        }
        if (Math.abs((audioEl.currentTime || 0) - startAtSec) < seekTolerance) {
          return true;
        }
        await new Promise((resolve) => setTimeout(resolve, seekPollMs));
      }
      return Math.abs((audioEl.currentTime || 0) - startAtSec) < seekTolerance;
    };

    if (isAudioPlaying || isAudioLoading) {
      clearUnmuteTimer();
      shouldKeepMutedRef.current = false;
      audioEl.pause();
      audioEl.muted = false;
      audioEl.currentTime = startAtSec;
      setIsAudioPlaying(false);
      setIsAudioLoading(false);
      return;
    }

    try {
      clearUnmuteTimer();
      shouldKeepMutedRef.current = true;
      audioEl.muted = true;
      setIsAudioLoading(true);

      if (audioEl.readyState < 1) {
        await new Promise((resolve) => {
          let done = false;
          const finalize = () => {
            if (done) return;
            done = true;
            audioEl.removeEventListener('loadedmetadata', onLoadedMetadata);
            resolve();
          };
          const onLoadedMetadata = () => {
            finalize();
          };
          audioEl.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
          audioEl.load();
          setTimeout(finalize, 5000);
        });
      }

      const prePlaySeekOk = await forceSeekToStart();
      if (playRequestIdRef.current !== playRequestId) {
        return;
      }
      await audioEl.play();
      const postPlaySeekOk = await forceSeekToStart();
      if (playRequestIdRef.current !== playRequestId) {
        audioEl.pause();
        return;
      }
      if (!(prePlaySeekOk || postPlaySeekOk)) {
        audioEl.pause();
        audioEl.currentTime = startAtSec;
        audioEl.muted = false;
        shouldKeepMutedRef.current = false;
        setIsAudioPlaying(false);
        setIsAudioLoading(false);
        return;
      }
      setIsAudioPlaying(true);
      setIsAudioLoading(false);
      if (audioEl.muted) {
        audioEl.muted = false;
      }
      shouldKeepMutedRef.current = false;
    } catch (error) {
      console.warn('Audio play failed:', error);
      clearUnmuteTimer();
      shouldKeepMutedRef.current = false;
      audioEl.muted = false;
      setIsAudioPlaying(false);
      setIsAudioLoading(false);
    }
  };

  return (
    <>
      <MessageBlockWrapper
        className={type === 'ai-talking-tutor' ? 'msg_block' : ''}
        isMe={isMe}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(newLineMessage);
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: simpleAudioButton && firstAudioUrl ? '10px' : '0px' }}>
          {simpleAudioButton && firstAudioUrl && false && (
            <>
              <button
                type="button"
                onClick={handleToggleAudio}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  marginTop: 0,
                  marginLeft: 2,
                  cursor: 'pointer',
                  color: '#1f1f1f',
                  width: '34px',
                  minWidth: '34px',
                  height: '34px',
                  flexShrink: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  overflow: 'visible',
                }}
                aria-label={isAudioPlaying || isAudioLoading ? 'Stop audio' : 'Play audio'}
              >
                {isAudioLoading ? (
                  <LoadingOutlined spin style={{ fontSize: '20px' }} />
                ) : isAudioPlaying ? (
                  <span
                    style={{
                      width: '14px',
                      height: '14px',
                      background: 'currentColor',
                      borderRadius: '2px',
                      display: 'inline-block',
                    }}
                  />
                ) : (
                  <PlayCircleFilled style={{ display: 'block', lineHeight: 1, verticalAlign: 'middle' }} />
                )}
              </button>
              <audio
                ref={audioRef}
                src={firstAudioUrl}
                preload="auto"
                onLoadedMetadata={() => {
                  if (!audioRef.current || isAudioPlaying || !(clipStartSec > 0)) {
                    return;
                  }
                  audioRef.current.currentTime = clipStartSec;
                }}
                onPlay={() => {
                  if (!audioRef.current || !(clipStartSec > 0)) {
                    return;
                  }
                  if (audioRef.current.currentTime < clipStartSec - 0.08) {
                    audioRef.current.currentTime = clipStartSec;
                  }
                }}
                onTimeUpdate={() => {
                  if (!audioRef.current) {
                    return;
                  }
                  if (shouldKeepMutedRef.current) {
                    const currentTime = Number(audioRef.current.currentTime) || 0;
                    if (!(clipStartSec > 0) || currentTime >= clipStartSec - 0.08) {
                      audioRef.current.muted = false;
                      shouldKeepMutedRef.current = false;
                      clearUnmuteTimer();
                    }
                  }
                  if (!isAudioPlaying || !Number.isFinite(clipEndSec)) {
                    return;
                  }
                  if (audioRef.current.currentTime >= clipEndSec - 0.02) {
                    audioRef.current.pause();
                    audioRef.current.muted = false;
                    shouldKeepMutedRef.current = false;
                    clearUnmuteTimer();
                    audioRef.current.currentTime = clipStartSec || 0;
                    setIsAudioPlaying(false);
                    setIsAudioLoading(false);
                  }
                }}
                onEnded={() => {
                  clearUnmuteTimer();
                  shouldKeepMutedRef.current = false;
                  setIsAudioPlaying(false);
                  setIsAudioLoading(false);
                  if (audioRef.current) {
                    audioRef.current.muted = false;
                    audioRef.current.currentTime = clipStartSec || 0;
                  }
                }}
                style={{ display: 'none' }}
              />
            </>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            {message?.length > 0 && (
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm, remarkBreaks]}
                children={newLineMessage}
                components={MarkdownComponents}
                transformLinkUri={(url) => {
                  if (url.startsWith('http')) {
                    return url;
                  }
                  let findIndx = currentFileList.findIndex((path) => path.endsWith(url));
                  if (findIndx !== -1) {
                    return currentFileList[findIndx];
                  } else if ((url.endsWith('.mp4') || url.endsWith('.mp3')) && url.startsWith('audio_')) {
                    let audioUrl = url.replace('audio_', '');
                    let findIndx = currentFileList.findIndex((path) => path.endsWith(audioUrl));
                    if (findIndx !== -1) {
                      return currentFileList[findIndx];
                    }
                  }
                  return url;
                }}
                transformImageUri={(url) => {
                  if (url.startsWith('http')) {
                    return url;
                  }

                  let findIndx = currentFileList.findIndex((path) => path.endsWith(url));
                  if (findIndx !== -1) {
                    return currentFileList[findIndx];
                  }
                  return url;
                }}
              />
            )}
          </div>
        </div>
        {urls.map((url, index) => {
          if (simpleAudioButton && url.type === 'audio') return null;
          if (url.type === 'image') return <Image rootClassName="msg_image" key={index} src={url.url} width={200} />;
          else if (url.type === 'video') return <video controls key={index} src={url.url} width={200} />;
          else if (url.type === 'audio') return <audio controls key={index} src={url.url} width={200} />;
          else return <Image rootClassName="msg_image" key={index} src={url.url} width={200} />;
        })}
      </MessageBlockWrapper>
    </>
  );
};

export default MessageBlock;
